import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Nfc, Loader2 } from 'lucide-react';
import { HTTPError } from 'ky';
import { ACTIVE_SESSION_KEY } from '@/hooks/useActiveSession';
import { acceptFobSessionOffer, dismissFobSessionOffer } from '@/lib/api';
import { toast } from '@/store/toast';
import styles from './AutochargeOfferCard.module.css';

/**
 * The tap-first fob offer (F3, Warren 2026-09-25: "an Invalid tag at a
 * building with the switch on becomes an offer in the app, the way
 * autocharge's after-the-fact offer already works").
 *
 * Rendered in flow on the charging screen, beside AutochargeOfferCard and in
 * the same shape, when the server says an unregistered fob was tapped on this
 * plug just before the driver started from the app. The client never sees
 * the tag: "Register it" sends the session id, the server re-derives the tag
 * from the charger log and returns an already-matched window, and the driver
 * lands on the ordinary paywall in Cards & fobs - the fee is named there
 * before anything is charged. "Not mine" dismisses on the server. Nothing
 * here starts or stops charging (ruling 3).
 */
export function FobOfferCard({ sessionId, onNavigate }: { sessionId: string; onNavigate: (path: string) => void }) {
  const { t } = useTranslation('rfid');
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'register' | 'dismiss' | null>(null);

  const register = async (): Promise<void> => {
    setBusy('register');
    try {
      const w = await acceptFobSessionOffer(sessionId);
      await qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      onNavigate(`/my-fobs?window=${encodeURIComponent(w._id)}`);
    } catch (err) {
      // The server's message, not ky's "Request failed with status code…".
      let message = t('offer.failed');
      if (err instanceof HTTPError) {
        message = await err.response.clone().json()
          .then((b: { error?: { message?: string } }) => b?.error?.message ?? message)
          .catch(() => message);
      }
      toast(message, { tone: 'error' });
      // A refused offer may have been retired server-side; refresh so a dead
      // card does not linger.
      void qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      setBusy(null);
    }
  };

  const dismiss = async (): Promise<void> => {
    setBusy('dismiss');
    try {
      await dismissFobSessionOffer(sessionId);
      await qc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
    } catch {
      setBusy(null);
    }
  };

  return (
    <section className={styles.card} aria-labelledby="fob-offer-title">
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden><Nfc size={18} /></span>
        <div>
          <h2 id="fob-offer-title" className={styles.title}>{t('offer.title')}</h2>
          <p className={styles.desc}>{t('offer.body')}</p>
        </div>
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={() => void dismiss()} disabled={busy !== null}>
          {t('offer.notMine')}
        </button>
        <button type="button" className={styles.primary} onClick={() => void register()} disabled={busy !== null}>
          {busy === 'register'
            ? <><Loader2 size={14} className={styles.spin} /> {t('offer.register')}</>
            : t('offer.register')}
        </button>
      </div>
    </section>
  );
}

/**
 * The way back to a taken-up offer's paywall. Shown while the server still
 * reports the window waiting at the payment step (fob_candidate.window_id);
 * a driver who left to add a card lands back on the same matched window.
 */
export function FobFinishLink({ windowId, onNavigate }: { windowId: string; onNavigate: (path: string) => void }) {
  const { t } = useTranslation('rfid');
  return (
    <section className={styles.card} aria-labelledby="fob-finish-title">
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden><Nfc size={18} /></span>
        <div>
          <h2 id="fob-finish-title" className={styles.title}>{t('flow.finishTitle')}</h2>
          <p className={styles.desc}>{t('flow.finishBody')}</p>
        </div>
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          onClick={() => onNavigate(`/my-fobs?window=${encodeURIComponent(windowId)}`)}
        >
          {t('flow.finishCta')}
        </button>
      </div>
    </section>
  );
}
